import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    Output,
    TemplateRef,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Coerce } from '@kalidea/kaligraphi';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, merge, NEVER, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, pairwise, tap } from 'rxjs/operators';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    ListingAboveSearchbarContentDirective
} from 'src/app/_shared/components/listing/listing-above-searchbar-content.directive';
import { ListingActionGroupedDirective } from 'src/app/_shared/components/listing/listing-action-grouped.directive';
import {
    ListingAfterSearchbarButtonsDirective
} from 'src/app/_shared/components/listing/listing-after-searchbar-buttons.directive';
import {
    ListingBetweenTableAndPagingDirective
} from 'src/app/_shared/components/listing/listing-between-table-and-paging.directive';
import { ListingFiltersComponent } from 'src/app/_shared/components/listing/listing-filters.component';
import { ListingFooterDirective } from 'src/app/_shared/components/listing/listing-footer.directive';
import { ListingHeaderDirective } from 'src/app/_shared/components/listing/listing-header.directive';
import {
    ListingSearchbarContentDirective
} from 'src/app/_shared/components/listing/listing-searchbar-content.directive';
import { ListingTitleDirective } from 'src/app/_shared/components/listing/listing-title.directive';
import { ListingDatasource } from 'src/app/_shared/components/listing/listing.datasource';
import {
    KalPaginatorComponent,
    KalTableComponent,
    KalTableDatasourceParameters,
    KalTableHeader,
    KalTableSortDirection
} from 'src/app/_shared/modules/kal-table/kal-table.module';
import {
    SharedListingResetAction,
    SharedListingSaveAction
} from 'src/app/_shared/stores/listing/shared-listing.actions';
import { SharedListingData, SharedListingState } from 'src/app/_shared/stores/listing/shared-listing.state';
import {
    ListingCountResultsLabelDirective
} from 'src/app/_shared/components/listing/listing-count-results-label.directive';
import { arrayEmpty, arrayNotEmpty } from 'src/app/utils/array.utils';
import { environment } from 'src/environments/environment';
import { isEmpty } from 'src/app/utils/object.utils';

/**
 * Pour une mise en page :
 * * **standard** avec un titre , des filtres et une table
 * <pre>
 *     // titre du listing
 *     <ng-template appListingTitle>Titre du listing</ng-template>
 *
 *     //  filtres du listing ( extending base class ListingFiltersComponent )
 *     <app-my-custom-listing-filters></app-my-custom-listing-filters>
 *
 *     // le listing en question
 *     <kal-table></kal-table>
 * </pre>
 *
 * * **avancée** avec une ligne de formulaire au dessus des boutons:
 * <pre>
 *      // au dessus des boutons de recherches
 *      <ng-template appListingAboveSearchbarContent>
 *          Ligne de formulaire
 *      </ng-template>
 *
 *      //on supprime le champs de recherche par défaut
 *      <ng-template appListingSearchbarContent></ng-template>
 *
 *      ...standard
 * </pre>
 */

@Component({
    selector: 'app-listing',
    templateUrl: './listing.component.html',
    styleUrls: ['./listing.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ListingComponent<T> extends BaseComponent implements AfterContentInit {

    static readonly keepFiltersParamName = 'listingKeepFilters';

    searchbarControl = new FormControl();

    countFilters: number;
    @ContentChild(KalTableComponent) table: KalTableComponent<T>;
    @ContentChild(forwardRef(() => ListingFiltersComponent)) filtersComponent: ListingFiltersComponent;
    @ContentChild(ListingTitleDirective) listingTitle: ListingTitleDirective;
    @ContentChild(ListingFooterDirective) listingFooter: ListingFooterDirective;
    @ContentChild(ListingHeaderDirective) listingHeader: ListingHeaderDirective;
    @ContentChild(ListingSearchbarContentDirective) searchbarContent: ListingSearchbarContentDirective;
    @ContentChild(ListingAboveSearchbarContentDirective) aboveSearchbarContent: ListingSearchbarContentDirective;
    @ContentChild(ListingAfterSearchbarButtonsDirective) afterSearchbarButtonsContent: ListingAfterSearchbarButtonsDirective;
    @ContentChild(ListingActionGroupedDirective) listingActionGrouped: ListingActionGroupedDirective;
    @ContentChild(ListingCountResultsLabelDirective) listingCountResultsLabel: ListingCountResultsLabelDirective;
    @ContentChild(ListingBetweenTableAndPagingDirective) betweenTableAndPaging: ListingBetweenTableAndPagingDirective;

    @Input() exportHeaders: KalTableHeader<T>[];
    @Input() exportName: string;
    @Input() searchBarPlaceholder: string;
    @Input() transformFilters: (searchbar?: string) => Record<string, any>;
    @Input() loadFiltersRelatedList: () => Observable<any>;
    @Input() checkSaveFilters: () => Observable<any>;
    @Input() paginationSize: number = KalPaginatorComponent.defaultPageSize;
    @Input() paginationPagesToDisplayList: number = KalPaginatorComponent.defaultPagesToDisplay;
    @Input() paginationRowsByPageList: number[] = KalPaginatorComponent.defaultRowsByPageList;
    @Input() kalLoadingMessage: string;
    @Input() @Coerce('boolean') paginate = true;
    @Input() @Coerce('boolean') displayCount = true;
    @Output() readonly export = new EventEmitter<void>();
    @Output() readonly paginationSizeChange = new EventEmitter<number>();
    private readonly areFiltersDisplayed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly loadingSubject$: Subject<boolean> = new Subject<boolean>();

    constructor(protected cdr: ChangeDetectorRef, private readonly route: ActivatedRoute) {
        super(cdr);
    }

    @HostBinding('class.displayedFilters')
    get displayFilters(): boolean {
        return this.areFiltersDisplayed.value;
    }

    get filtersDisplayed$(): Observable<boolean> {
        return this.areFiltersDisplayed.asObservable();
    }

    private _key: string;

    get key(): string {
        return this._key;
    }

    // uniq Id for this listing
    @Input()
    set key(key: string) {
        this._key = key;
        // if key updates, we should propagate this update to related component
        this.injectRequiredDataInSubComponent();
    }

    get titleTemplate(): TemplateRef<any> {
        return this.listingTitle?.template;
    }

    get footerTemplate(): TemplateRef<any> {
        return this.listingFooter?.template;
    }

    get headerTemplate(): TemplateRef<any> {
        return this.listingHeader?.template;
    }

    get searchbarContentTemplate(): TemplateRef<any> {
        return this.searchbarContent?.template;
    }

    get aboveSearchbarContentTemplate(): TemplateRef<any> {
        return this.aboveSearchbarContent?.template;
    }

    get betweenTableAndPagingTemplate(): TemplateRef<any> {
        return this.betweenTableAndPaging?.template;
    }

    get afterSearchbarButtonsTemplate(): TemplateRef<any> {
        return this.afterSearchbarButtonsContent?.template;
    }

    get loading$(): Observable<boolean> {
        return this.loadingSubject$.asObservable();
    }

    get dataSource(): ListingDatasource<T> {
        return this.table.dataSource as ListingDatasource<T>;
    }

    public get listingStoreInformations(): SharedListingData {
        return this.snapshot(SharedListingState.informations(this.key));
    }

    public get filters(): any {
        return this.listingStoreInformations.filters;
    }

    get canDisplaySearchBar(): boolean {
        const selection = this.table?.selection;
        return !selection || selection.isEmpty() || !this.listingActionGrouped?.template;
    }

    applyFilters(overridedFilters = {}): void {
        this.watch$ = this.checkFilters$().pipe(
            tap(() => {
                this.saveFilters(overridedFilters);
            })
        );
    }

    refresh(config: KalTableDatasourceParameters): void {
        if (!config) {
            this.dataSource.page.first();
            this.dataSource.filters.set(null);
            this.dataSource.sort.set(null);
        } else {
            this.dataSource.parameters = config;
        }
    }

    resetFilters(): void {
        this.watch$ = this.checkFilters$().pipe(
            tap(() => {
                this.filtersComponent?.resetFilters();
                this.searchbarControl.patchValue('');
                this.saveFilters();
            })
        );
    }

    toggleFilters(): void {
        this.areFiltersDisplayed.next(!this.displayFilters);
    }

    closeFilters(): void {
        this.areFiltersDisplayed.next(false);
        setTimeout(() => this.cdr.markForCheck(), 100);
    }

    exportData(): void {
        if (arrayEmpty(this.export.observers) && arrayNotEmpty(this.exportHeaders)) {
            this.dataSource.exportAsCSV(this.exportHeaders, this.exportName);
            return;
        }

        this.export.emit();
    }

    cancelFilters(): void {
        this.closeFilters();
        this.filtersComponent?.cancelFilters();
    }

    updateForm(filters: Record<string, any>): void {
        this.filtersComponent?.updateForm(filters);
    }

    saveFilters(overridedFilters = {}): void {
        // hide filters
        this.closeFilters();

        // transform filters with local ListingFiltersComponent
        this.searchbarControl.patchValue(this.searchbarControl.value?.trim());
        const component = this.transformFilters ? this : this.filtersComponent;
        const filters = {
            ...component?.transformFilters(this.searchbarControl.value),
            ...overridedFilters
        };
        this.dataSource.page.set({
            number: 0,
            size: this.paginationSize
        });
        this.dataSource.filters.set(filters);
    }

    refreshSelectionNumberOfItems(): void {
        if (this.table?.selection) {
            this.table.selection.numberOfItems = this.dataSource.nbOfElements;
        }
    }

    private checkFilters$(): Observable<any> {
        let action$ = of(null);

        if (this.filtersComponent) {
            action$ = this.filtersComponent.checkSaveFilters$();
        } else if (!!this.checkSaveFilters) {
            action$ = this.checkSaveFilters();
        }

        return action$;
    }

    private checkRequirements(): void {

        const errors: string[] = [];

        const {dataSource} = this.table;
        if (!(dataSource instanceof ListingDatasource)) {
            errors.push('datasource should be of type ListingDatasource');
            console.error(dataSource);
        }

        const listingFiltersComponentMethod: (keyof ListingFiltersComponent)[] = [
            'transformFilters',
            'cancelFilters',
            'resetFilters',
        ];
        if (this.filtersComponent && listingFiltersComponentMethod.some(m => !this.filtersComponent?.[m])) {
            errors.push(`filters component should extends abstract ListingFiltersComponent ( missing : ${listingFiltersComponentMethod.find(m => !this.filtersComponent?.[m])})`);
        }

        if (errors.length > 0) {
            throw Error(`Inconsistency for listing [key]=${this.key}: ${errors.join(',\n')}`);
        }
    }

    private setInitialFilters(): void {
        // should we reset filters ?
        const keepFilters = this.route.snapshot.queryParams[ListingComponent.keepFiltersParamName] || 0;
        let action$ = of(null);

        // set searchbar value
        if (this.filtersComponent) {
            action$ = this.filtersComponent.loadFilterRelatedList$();
            this.searchbarControl.patchValue(this.filtersComponent.searchBarValue());
        } else if (!!this.loadFiltersRelatedList) {
            action$ = this.loadFiltersRelatedList();
        }

        this.watch$ = action$.pipe(
            tap(() => {
                if (+keepFilters === 0 || isEmpty(this.listingStoreInformations)) {
                    this.watch$ = this.dispatch$(new SharedListingResetAction(this.key));
                    this.resetFilters();
                } else {
                    const {page, sort, filters} = this.listingStoreInformations;

                    // avec le flag keepFilter on a aussi les données de pagination en mémoire.
                    // on synchronise le kal-paginator lors du chargement d'une page si elle doit garder ses paramètres
                    if(!!page && page.size !== this.paginationSize) {
                        this.paginationSize = page.size;
                    }

                    this.dataSource.page.set(page || {number: 0, size: this.paginationSize});
                    this.dataSource.sort.set(sort || {code: '', direction: KalTableSortDirection.DESC});
                    this.dataSource.filters.set(filters || null);
                }
            })
        );
    }

    private initSubscription(): void {
        this.watch$ = this.dataSource.changesWithoutLoading$.pipe(
            debounceTime(environment.debounceTime),
            pairwise(),
            tap(values => {
                const [prev, curr] = values;

                if (!isEqual(prev, curr)) {
                    this.dispatch$(new SharedListingSaveAction(this.key, this.dataSource.parameters));
                }
            })
        );

        // À chaque changement des données du datasource,
        // il faut redonner à la selection le nombre d'éléments total dont elle peut disposer
        this.watch$ = this.dataSource.unfilteredRows$.pipe(
            tap(() => this.refreshSelectionNumberOfItems())
        )
    }

    private trackUpdates(): void {

        // watch for selection & datasource updates
        this.watch$ = merge(this.dataSource.changes$, this.table.selection?.changes || NEVER).pipe(
            tap(() => this.cdr.markForCheck())
        );

        // update disable state from searchbarControl when datasource is loading
        this.watch$ = this.dataSource.loading.change$.pipe(
            tap(loading => {
                this.searchbarControl[loading ? 'disable' : 'enable']();
                this.cdr.markForCheck();
            })
        );

        if (this.filtersComponent?.form) {
            this.watch$ = this.filtersComponent.form.valueChanges.pipe(
                distinctUntilChanged(isEqual),
                tap(() => this.cdr.markForCheck())
            );
        }
    }

    private injectRequiredDataInSubComponent(): void {
        if (this.filtersComponent) {
            this.filtersComponent.key = this.key;

            // set only once
            if (!this.filtersComponent.datasource) {
                this.filtersComponent.datasource = this.dataSource;
            }
        }
    }

    ngAfterContentInit(): void {
        // ensure all requirements are met
        this.checkRequirements();

        // Inject required data in sub components ( first time, filters are not ready );
        this.injectRequiredDataInSubComponent();

        // set initial filters
        this.setInitialFilters();

        // track and manage updates
        this.trackUpdates();

        this.initSubscription();
    }
}
